<script setup lang="ts">
/**
 * Упоминания в шапке: «@» с бейджем количества. Панель — список (чат, кто, когда, текст),
 * клик — модалка с контекстом и переходом в Telegram. Шестерёнка — через сколько минут упоминание пропадает.
 * Видно только тем, чей пользователь привязан к сотруднику с username.
 */
import { computed, onMounted, ref } from 'vue';
import type { MentionDto } from '@sync-tracker/shared';
import { formatRelative } from '@/lib/format';
import { useNow } from '@/lib/now';
import { MENTION_WINDOWS, mentionKey, useMentions } from '@/stores/mentions';
import { Icon, UiButton } from '@/ui';
import { usePopover } from '@/ui/popover';
import MentionContextModal from './MentionContextModal.vue';

const mentions = useMentions();
const now = useNow();
onMounted(() => mentions.start());

const trigger = ref<HTMLElement | null>(null);
const { open, panel, pos, toggle, hide } = usePopover(trigger, { width: () => 380, maxHeight: 480 });

const count = computed(() => mentions.visible.length);
const settings = ref(false);
const custom = ref<number | null>(null);
const isPreset = computed(() => (MENTION_WINDOWS as readonly number[]).includes(mentions.windowMin));
function applyCustom() {
  if (custom.value && custom.value > 0) mentions.setWindow(custom.value);
}

const selected = ref<MentionDto | null>(null);
function openMention(m: MentionDto) {
  selected.value = m;
  hide();
}

/** Текст с подсвеченным @username (без v-html — части рендерятся шаблоном). */
function parts(text: string): { text: string; me: boolean }[] {
  const u = mentions.username;
  if (!u) return [{ text, me: false }];
  const re = new RegExp(`(@${u})(?![A-Za-z0-9_])`, 'gi');
  return text.split(re).filter(Boolean).map((t) => ({ text: t, me: t.toLowerCase() === `@${u}`.toLowerCase() }));
}
</script>

<template>
  <template v-if="mentions.username">
    <button
      ref="trigger"
      type="button"
      class="bell"
      :class="{ on: count, open }"
      :title="count ? `Вас упомянули: ${count}` : `Упоминания @${mentions.username}`"
      @click="toggle"
    >
      <Icon name="at" :size="16" />
      <span v-if="count" class="badge">{{ count > 99 ? '99+' : count }}</span>
    </button>

    <Teleport to="body">
      <Transition name="pop">
        <div
          v-if="open"
          ref="panel"
          class="panel"
          :style="{ top: pos.top + 'px', left: pos.left + 'px', width: pos.width + 'px', maxHeight: pos.maxHeight + 'px' }"
        >
          <header class="head">
            <div class="grow">
              <div class="title">Упоминания</div>
              <div class="faint small">@{{ mentions.username }} · за {{ mentions.windowMin }} мин</div>
            </div>
            <button v-if="count" type="button" class="link small" @click="mentions.dismissAll()">Скрыть все</button>
            <button type="button" class="icon-btn" :class="{ on: settings }" title="Сколько показывать упоминание" @click="settings = !settings">
              <Icon name="settings" :size="15" />
            </button>
          </header>

          <div v-if="settings" class="settings">
            <div class="small muted">Упоминание пропадает через</div>
            <div class="chips">
              <button
                v-for="w in MENTION_WINDOWS"
                :key="w"
                type="button"
                class="chip"
                :class="{ on: mentions.windowMin === w }"
                @click="mentions.setWindow(w)"
              >
                {{ w }} мин
              </button>
            </div>
            <div class="custom">
              <input
                v-model.number="custom"
                type="number"
                min="1"
                max="1440"
                :placeholder="isPreset ? 'Своё, мин' : `${mentions.windowMin} мин`"
                @keydown.enter="applyCustom"
              />
              <UiButton size="sm" :disabled="!custom" @click="applyCustom">Применить</UiButton>
            </div>
          </div>

          <div class="list">
            <div
              v-for="m in mentions.visible"
              :key="mentionKey(m)"
              class="item"
              role="button"
              tabindex="0"
              @click="openMention(m)"
              @keydown.enter="openMention(m)"
            >
              <div class="item-top">
                <span class="chat ellipsis">{{ m.chatTitle }}</span>
                <span class="faint small">{{ formatRelative(m.date, now) }}</span>
                <button type="button" class="x" title="Скрыть" @click.stop="mentions.dismiss(m)"><Icon name="close" :size="12" /></button>
              </div>
              <div class="who small muted">{{ m.fromName ?? m.fromUsername ?? '—' }}</div>
              <div class="text clamp-2">
                <template v-for="(p, i) in parts(m.text)" :key="i"><mark v-if="p.me">{{ p.text }}</mark><template v-else>{{ p.text }}</template></template>
              </div>
            </div>
            <div v-if="!mentions.visible.length" class="empty">
              <Icon name="at" :size="18" />
              <span>Упоминаний за {{ mentions.windowMin }} мин нет</span>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <MentionContextModal :mention="selected" :username="mentions.username" @close="selected = null" @dismiss="(m) => mentions.dismiss(m)" />
  </template>
</template>

<style scoped>
.bell {
  position: relative;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--surface-2);
  color: var(--muted);
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: all var(--dur) var(--ease);
}
.bell:hover,
.bell.open {
  color: var(--text);
  border-color: var(--border-strong);
}
.bell.on {
  color: var(--accent);
  border-color: color-mix(in srgb, var(--accent) 45%, var(--border));
  background: color-mix(in srgb, var(--accent) 10%, var(--surface-2));
}
.badge {
  position: absolute;
  top: -6px;
  right: -7px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--danger);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: grid;
  place-items: center;
  box-shadow: 0 0 0 2px var(--bg);
}
.panel {
  position: fixed;
  z-index: 210;
  display: flex;
  flex-direction: column;
  background: var(--surface);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
}
.head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 12px 10px 14px;
  border-bottom: 1px solid var(--divider);
}
.title {
  font-weight: 600;
}
.link {
  border: none;
  background: transparent;
  color: var(--accent);
  cursor: pointer;
  padding: 3px 6px;
  border-radius: 6px;
}
.link:hover {
  background: var(--surface-hover);
}
.icon-btn {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--muted);
  display: grid;
  place-items: center;
  cursor: pointer;
}
.icon-btn:hover,
.icon-btn.on {
  background: var(--surface-hover);
  color: var(--text);
}
.settings {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 14px 12px;
  border-bottom: 1px solid var(--divider);
  background: var(--surface-2);
}
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.chip {
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text-2);
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 12px;
  cursor: pointer;
}
.chip.on {
  border-color: var(--accent);
  color: var(--accent);
  background: color-mix(in srgb, var(--accent) 10%, var(--surface));
  font-weight: 600;
}
.custom {
  display: flex;
  gap: 6px;
}
.custom input {
  flex: 1;
  min-width: 0;
  height: 28px;
  padding: 0 10px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  background: var(--surface);
  color: var(--text);
}
.list {
  overflow: auto;
  padding: 6px;
}
.item {
  padding: 9px 10px;
  border-radius: 8px;
  cursor: pointer;
}
.item:hover,
.item:focus-visible {
  background: var(--surface-hover);
  outline: none;
}
.item-top {
  display: flex;
  align-items: center;
  gap: 8px;
}
.chat {
  flex: 1;
  min-width: 0;
  font-weight: 600;
  font-size: 13px;
}
.x {
  border: none;
  background: transparent;
  color: var(--faint);
  width: 20px;
  height: 20px;
  border-radius: 5px;
  display: grid;
  place-items: center;
  cursor: pointer;
  opacity: 0;
}
.item:hover .x {
  opacity: 1;
}
.x:hover {
  background: var(--surface-3);
  color: var(--text);
}
.who {
  margin-top: 1px;
}
.text {
  margin-top: 3px;
  font-size: 13px;
  color: var(--text-2);
  word-break: break-word;
}
mark {
  background: color-mix(in srgb, var(--accent) 18%, transparent);
  color: var(--accent);
  border-radius: 4px;
  padding: 0 2px;
  font-weight: 600;
}
.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 26px 12px;
  color: var(--faint);
  font-size: 12.5px;
}
.pop-enter-active,
.pop-leave-active {
  transition:
    opacity 120ms var(--ease),
    transform 120ms var(--ease);
}
.pop-enter-from,
.pop-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
